/**
 * Box Rips / Breaks view — opened sealed lots and what came out of them.
 *
 * Money columns, honestly labeled (2026-07-11 overhaul):
 *   Pull FMV (at rip) — the pull-value snapshot. AUTO-TRACKS the live card
 *     values until the first pull is sold/traded, then freezes (so a
 *     profitable box keeps showing its win). Recapture in Edit Rip can
 *     deliberately refresh it later.
 *   Held (live)   — value of pulled cards still in inventory right now.
 *   Realized      — proceeds already collected: sale prices + trade credit.
 *   Outcome       — the honest bottom line: Realized + Held + bulk − box cost.
 *
 * None of these are income by themselves — profit books when pulls actually
 * sell or trade. EV/Outcome are analytics for "was this box worth opening?".
 *
 * Interaction matches the inventory tabs: click a row to open the rip.
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar } from "../utils/format";
import { currentPullFMVForRip, realizedProceedsForRip, lockedPullFMVForRip, trueOutcomeForRip, ripHasDispositions } from "../lib/derived";

/** Row click opens the rip editor; inner links keep their own behavior. */
export function ripRowClick(e: MouseEvent, ripId: string): void {
  const t = e.target as HTMLElement | null;
  if (t && t.closest("a, button, input, select, label")) return;
  (window as any).editRipMeta?.(ripId);
}
(window as any).ripRowClick = ripRowClick;

export function viewRips(root: HTMLElement): void {
  const rips = DB.table("rips").slice().reverse();

  const totalBasisRemoved = sum(rips, r => r["Basis Removed"]);
  const totalPullFMVAtRip = sum(rips, r => lockedPullFMVForRip(r));
  const totalHeld         = sum(rips, r => currentPullFMVForRip(r["Rip ID"]));
  const totalRealized     = sum(rips, r => realizedProceedsForRip(r["Rip ID"]));
  const totalOutcome      = sum(rips, r => trueOutcomeForRip(r));

  const rips_total = rips.length;
  const rips_page = slicePage("rips", rips, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Box Cost (removed)", value: fmt$0(totalBasisRemoved) },
      { label: "Pull FMV (at rip)",  value: fmt$0(totalPullFMVAtRip) },
      { label: "Held (live)",        value: fmt$0(totalHeld) },
      { label: "Realized",           value: fmt$0(totalRealized) },
      { label: "Outcome",            value: fmt$0(totalOutcome), tone: totalOutcome >= 0 ? "green" : "red" },
    ])}
    <div class="text-xs text-[var(--muted)] mb-3 px-1">
      <b>Pull FMV (at rip)</b> auto-tracks live card values until the first pull sells or trades, then freezes.
      <b>Held</b> is the live value of pulls still in inventory. <b>Realized</b> is money already collected from pulls (sales + trade credit).
      <b>Outcome = Realized + Held + bulk − box cost</b> — the honest "was this box worth it?" number.
      None of these are income by themselves; profit books when pulls sell. Click a rip to open it (Recapture lives there).
    </div>
    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th data-col="Rip ID">Rip ID</th><th data-col="Date">Date</th>
          <th data-col="Product" class="cell-clip">Product</th>
          <th data-col="Qty" class="text-right">Qty</th>
          <th data-col="Box Cost" class="text-right">Box Cost</th>
          <th data-col="Pull FMV (at rip)" class="text-right">Pull FMV (at rip)</th>
          <th data-col="Held" class="text-right">Held</th>
          <th data-col="Realized" class="text-right">Realized</th>
          <th data-col="Outcome" class="text-right">Outcome</th>
          <th data-col="Pulls">Pulls</th>
        </tr></thead>
        <tbody>
          ${rips_page.map(r => {
            const id = escapeHtml(r["Rip ID"] || "");
            const ripId = r["Rip ID"] || "";
            const sealedId = escapeHtml(r["Sealed Lot ID"] || "");
            const lockedFMV  = lockedPullFMVForRip(r);
            const heldFMV    = currentPullFMVForRip(ripId);
            const realized   = realizedProceedsForRip(ripId);
            const outcome    = trueOutcomeForRip(r);
            const frozen     = ripHasDispositions(ripId);
            const pullCount  = DB.table("cards").filter(c => c["Source ID"] === ripId).length;
            const bulkPulls: any[] = Array.isArray(r["Bulk Pulls"]) ? r["Bulk Pulls"] : [];
            const bulkQty = bulkPulls.reduce((s, b) => s + (Number(b["Qty"]) || 0), 0);
            return `<tr data-selectable onclick="ripRowClick(event, '${id}')" title="Click to open this rip">
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${id}')">${id}</a></td>
              <td>${escapeHtml(fmtDate(r["Date"]))}</td>
              <td class="cell-clip">
                <div class="truncate">${escapeHtml(r["Sealed Product"] || "")}</div>
                ${sealedId ? `<a class="font-mono text-xs text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${sealedId}')">${sealedId}</a>` : ""}
              </td>
              <td class="text-right stat-num">${fmtN(r["Qty Opened"])}</td>
              <td class="text-right stat-num">${fmt$(r["Basis Removed"])}</td>
              <td class="text-right stat-num" title="${frozen ? "Frozen at the first sale/trade of a pull. Open the rip and use Recapture to refresh deliberately." : "Auto-tracking live card values — freezes at the first sale/trade of a pull."}">${frozen ? "" : `<span class="text-xs text-[var(--muted)]" title="Auto-tracking until first disposition">\u25CB</span> `}${fmt$(lockedFMV)}</td>
              <td class="text-right stat-num text-[var(--muted)]" title="Live value of pulled cards still in inventory.">${fmt$(heldFMV)}</td>
              <td class="text-right stat-num" title="Proceeds already collected from pulls: sale prices + trade credit (basis + realized gain).">${fmt$(realized)}</td>
              <td class="text-right stat-num font-semibold ${outcome >= 0 ? "text-emerald-300" : "text-rose-300"}" title="Realized + Held + bulk line totals − box cost.">${fmt$(outcome)}</td>
              <td class="text-xs text-[var(--muted)]">${fmtN(pullCount)} card${pullCount === 1 ? "" : "s"}${bulkQty ? ` \u00b7 ${fmtN(bulkQty)} bulk` : ""}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="10" class="text-center text-[var(--muted)] py-8">No rips logged. Check a sealed lot and hit Generate Rip, or open one from its drawer.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("rips", rips_total, 50)}
  `;
}
